import { useEffect, useState } from 'react';
import { Banknote, Loader2, X, Check, Search } from 'lucide-react';
import supabase from '../lib/supabase';

type Transfer = {
  id: string;
  court_booking_id: string | null;
  coach_booking_id: string | null;
  club_id: string | null;
  coach_id: string | null;
  stripe_transfer_id: string | null;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  clubs: { name: string } | null;
  coaches: { display_name: string } | null;
};

type Filter = 'all' | 'clubs' | 'coaches';

type RecipientOption = {
  kind: 'club' | 'venue' | 'coach';
  id: string;
  label: string;
  stripeAccountId: string;
};

export default function Payouts() {
  const [rows, setRows] = useState<Transfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('all');
  const [showPayout, setShowPayout] = useState(false);

  const load = async () => {
    setLoading(true);
    let q = supabase
      .from('stripe_transfers')
      .select('*, clubs(name), coaches(display_name)')
      .order('created_at', { ascending: false })
      .limit(200);
    if (filter === 'clubs') q = q.not('club_id', 'is', null);
    if (filter === 'coaches') q = q.not('coach_id', 'is', null);
    const { data } = await q;
    setRows((data as unknown as Transfer[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]);

  const exportCSV = () => {
    const header = ['date', 'recipient_type', 'recipient', 'amount', 'currency', 'status', 'stripe_transfer_id', 'booking_type'];
    const lines = [header.join(',')].concat(
      rows.map((r) => {
        const type = r.club_id ? 'club' : 'coach';
        const name = r.clubs?.name ?? r.coaches?.display_name ?? '';
        const bookingType = r.court_booking_id ? 'court'
          : r.coach_booking_id ? 'coach_booking' : 'other';
        return [
          new Date(r.created_at).toISOString(),
          type,
          JSON.stringify(name),
          r.amount,
          r.currency,
          r.status,
          r.stripe_transfer_id ?? '',
          bookingType,
        ].join(',');
      }),
    );
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payouts-${filter}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl">Payouts</h1>
          <p className="text-sm text-gray-500">Transfers to club and coach Stripe Connect accounts.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowPayout(true)}
            className="h-10 px-4 rounded-lg border border-gray-300 hover:border-gray-500 text-sm inline-flex items-center gap-2"
          >
            <Banknote className="w-4 h-4" /> Pay out now
          </button>
          <button
            onClick={exportCSV}
            disabled={rows.length === 0}
            className="h-10 px-4 rounded-lg bg-[#2d5016] text-white text-sm disabled:opacity-50"
          >
            Export CSV
          </button>
        </div>
      </div>

      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg mb-4 w-fit">
        {(['all', 'clubs', 'coaches'] as Filter[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`h-8 px-4 rounded-md text-sm capitalize ${
              filter === f ? 'bg-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Recipient</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Stripe ID</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                  {loading ? 'Loading…' : 'No payouts yet.'}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 text-gray-500">
                    {new Date(r.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    {r.clubs?.name ?? r.coaches?.display_name ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-xs uppercase text-gray-500">
                    {r.club_id ? 'Club' : r.coach_id ? 'Coach' : '—'}
                    {r.court_booking_id && <span className="ml-1 text-gray-400">· court</span>}
                    {r.coach_booking_id && <span className="ml-1 text-gray-400">· lesson</span>}
                  </td>
                  <td className="px-4 py-3 text-right">{r.currency} {Number(r.amount).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                      r.status === 'paid' ? 'bg-green-100 text-green-800'
                      : r.status === 'failed' ? 'bg-red-100 text-red-800'
                      : 'bg-amber-100 text-amber-800'
                    }`}>{r.status}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">
                    {r.stripe_transfer_id ?? '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showPayout && (
        <ManualPayoutModal
          onClose={() => setShowPayout(false)}
          onDone={() => { setShowPayout(false); load(); }}
        />
      )}
    </div>
  );
}

/**
 * Manual "pay out now" — pick a club / venue / coach with an
 * onboarded Stripe account and trigger an immediate payout from
 * their Stripe balance to their bank, bypassing the connected
 * account's default schedule.
 *
 * Recipient options are loaded from clubs / club_venues / coaches
 * filtered to rows that have stripe_onboarding_completed_at set.
 * Without that, Stripe doesn't yet have a destination bank account
 * for the payout to land in.
 */
function ManualPayoutModal({
  onClose, onDone,
}: { onClose: () => void; onDone: () => void }) {
  const [options, setOptions] = useState<RecipientOption[]>([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<RecipientOption | null>(null);
  const [amount, setAmount] = useState(''); // AED, blank = full balance
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{
    payoutId: string; amount: number; currency: string; arrivalDate: number; status: string;
  } | null>(null);

  useEffect(() => {
    (async () => {
      const [clubsRes, venuesRes, coachesRes] = await Promise.all([
        supabase
          .from('clubs')
          .select('id, name, stripe_account_id, stripe_onboarding_completed_at')
          .not('stripe_account_id', 'is', null)
          .not('stripe_onboarding_completed_at', 'is', null),
        (supabase.from as any)('club_venues')
          .select('id, name, stripe_account_id, stripe_onboarding_completed_at, clubs:club_id(name)')
          .not('stripe_account_id', 'is', null)
          .not('stripe_onboarding_completed_at', 'is', null),
        supabase
          .from('coaches')
          .select('id, display_name, stripe_account_id, stripe_onboarding_completed_at')
          .not('stripe_account_id', 'is', null)
          .not('stripe_onboarding_completed_at', 'is', null),
      ]);
      const next: RecipientOption[] = [
        ...((clubsRes.data ?? []) as any[]).map((c) => ({
          kind: 'club' as const, id: c.id, label: c.name, stripeAccountId: c.stripe_account_id,
        })),
        ...((venuesRes.data ?? []) as any[]).map((v) => ({
          kind: 'venue' as const,
          id: v.id,
          label: `${v.clubs?.name ?? 'Venue'} — ${v.name}`,
          stripeAccountId: v.stripe_account_id,
        })),
        ...((coachesRes.data ?? []) as any[]).map((c) => ({
          kind: 'coach' as const, id: c.id, label: c.display_name, stripeAccountId: c.stripe_account_id,
        })),
      ];
      setOptions(next);
    })();
  }, []);

  const filtered = search.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(search.trim().toLowerCase()))
    : options;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) { setErr('Pick a recipient first.'); return; }
    setBusy(true); setErr(null);
    // Stripe API expects amount in fils (AED * 100). Convert here so
    // the form stays in AED for human readability.
    const amountFils = amount.trim()
      ? Math.round(Number(amount) * 100)
      : undefined;
    if (amount.trim() && (!Number.isFinite(amountFils!) || (amountFils ?? 0) <= 0)) {
      setBusy(false); setErr('Amount must be a positive number.'); return;
    }
    const { data, error } = await supabase.functions.invoke('admin-trigger-payout', {
      body: {
        stripeAccountId: selected.stripeAccountId,
        amount: amountFils,
        currency: 'aed',
      },
    });
    setBusy(false);
    if (error) {
      // The function returns 422 with a structured body when there's
      // no available balance; surface that explicitly.
      const ctx: any = (error as any).context;
      let detail: string | null = null;
      try {
        const bodyText = typeof ctx?.body === 'string' ? ctx.body : null;
        const parsed = bodyText ? JSON.parse(bodyText) : null;
        detail = parsed?.error ?? null;
      } catch { /* fall through */ }
      setErr(detail ?? (error as any).message ?? 'Payout failed.');
      return;
    }
    setResult(data as any);
  };

  if (result) {
    const arrival = new Date(result.arrivalDate * 1000);
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold">Payout triggered</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Stripe acknowledged the payout. It clears to the recipient's bank on the
                arrival date below.
              </p>
            </div>
            <button onClick={onDone} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between">
              <dt className="text-gray-500">Amount</dt>
              <dd>{result.currency} {result.amount.toFixed(2)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Stripe payout id</dt>
              <dd className="font-mono text-xs">{result.payoutId}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Status</dt>
              <dd className="capitalize">{result.status}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Expected arrival</dt>
              <dd>{arrival.toLocaleDateString()}</dd>
            </div>
          </dl>
          <div className="flex justify-end pt-4">
            <button onClick={onDone} className="h-10 px-5 rounded-lg bg-[#2d5016] text-white text-sm">Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">Pay out now</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Trigger an immediate payout, bypassing the recipient's default schedule.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <div className="text-xs text-gray-600 mb-1">Recipient</div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={selected ? selected.label : search}
                onChange={(e) => { setSelected(null); setSearch(e.target.value); }}
                placeholder="Search clubs, venues, coaches"
                className="w-full h-10 pl-9 pr-3 rounded-lg border border-gray-300 text-sm"
              />
            </div>
            {!selected && search.trim() && (
              <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-gray-200">
                {filtered.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-500">No onboarded recipients match.</div>
                ) : (
                  filtered.slice(0, 12).map((o) => (
                    <button
                      key={`${o.kind}:${o.id}`}
                      type="button"
                      onClick={() => { setSelected(o); setSearch(''); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium">{o.label}</div>
                      <div className="text-xs text-gray-500 capitalize">{o.kind} · {o.stripeAccountId}</div>
                    </button>
                  ))
                )}
              </div>
            )}
          </label>

          <label className="block">
            <div className="text-xs text-gray-600 mb-1">Amount (AED)</div>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Leave blank to pay out the full available balance"
              className="w-full h-10 px-3 rounded-lg border border-gray-300 text-sm"
            />
            <div className="text-xs text-gray-400 mt-1">
              Stripe rejects amounts above the recipient's available balance. Leave blank
              to drain the AED balance entirely.
            </div>
          </label>

          {err && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-10 rounded-lg border border-gray-300 text-sm">
              Cancel
            </button>
            <button type="submit" disabled={busy || !selected}
              className="flex-1 h-10 rounded-lg bg-[#2d5016] text-white text-sm flex items-center justify-center gap-1 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Pay out
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
